import { ReplaySubject, first, firstValueFrom } from 'rxjs';
import type { Cluster } from '@googlemaps/markerclusterer';

import { assertValue } from '@libs/utils';

import { GOOGLE_API_KEY } from '../../helpers/constants';
import {
  getSingleMapContainer,
  setSingleMapContainer,
  setGoogleMapInstance,
  updateMapInstanceOptions,
  putMapInContainer,
} from '../../helpers/mapInstance';
import type {
  MapDynamicIntegration,
  MapInitializationOptions,
  MapIntegrationType,
  Marker,
  MountedDynamicMap,
} from '../../maps';
import {
  defaultMarkerFormatter,
  defaultClusterMarkerFormatter,
} from '../../markerFormatter';
import type {
  MarkerFormatter,
  ClusterMarkerFormatter,
} from '../../markerFormatter';
import { GoogleMapsLoader } from '../loader/googleMapsLoader';
import type { ClusterMarkerFactory, SingleMarkerFactory } from '../types';

import { MountedGoogleMap } from './mountedDynamicMap';

type LoaderStatus = 'idle' | 'loading' | 'loaded';
let loaderStatus: LoaderStatus = 'idle';
const loaderStatusSubject = new ReplaySubject<LoaderStatus>();
loaderStatusSubject.subscribe((status) => (loaderStatus = status));

let googleMapInstance: google.maps.Map | undefined;

// eslint-disable-next-line import/no-mutable-exports
export let googleMapElement: HTMLElement | null;

export class GoogleMapsIntegration
  implements MapDynamicIntegration<MapIntegrationType.GoogleMaps>
{
  public async createDynamicMapIntegration(
    element: HTMLElement,
    options?: MapInitializationOptions
  ): Promise<MountedDynamicMap<MapIntegrationType.GoogleMaps>> {
    if (loaderStatus === 'idle') {
      await this.loadMapsLibrary(options);
    }

    await firstValueFrom(
      loaderStatusSubject.pipe(first((status) => status === 'loaded'))
    );

    googleMapElement = getSingleMapContainer();

    if (!googleMapInstance || !googleMapElement) {
      googleMapElement = setSingleMapContainer();
      googleMapInstance = setGoogleMapInstance(googleMapElement, options);
    } else {
      updateMapInstanceOptions(googleMapInstance, options);
    }

    putMapInContainer(element, googleMapElement);

    const singleMarkerFactory: SingleMarkerFactory = (
      marker: Marker,
      position: google.maps.LatLng
    ): google.maps.marker.AdvancedMarkerElement => {
      let markerFormatter: MarkerFormatter;
      if (
        marker.useContentFormatter &&
        options?.markerFormatters &&
        options.markerFormatters[marker.useContentFormatter]
      ) {
        const formatter = options.markerFormatters[marker.useContentFormatter];
        assertValue(
          formatter,
          'dynamicMapIntegration.ts > singleMarkerFactory > formatter'
        );
        markerFormatter = formatter;
      } else {
        markerFormatter = defaultMarkerFormatter;
      }

      const content = markerFormatter(marker, { integration: 'google' });
      const displayPriority = marker.displayPriority
        ? marker.displayPriority + 1
        : 1;

      const singleMarker = new google.maps.marker.AdvancedMarkerElement({
        position,
        content,
      });

      singleMarker.element.classList.add('google-marker-container');
      singleMarker.element.style.zIndex = displayPriority.toString();

      return singleMarker;
    };

    const clusterMarkerFactory: ClusterMarkerFactory = (
      marker: Marker,
      cluster: Cluster,
      position: google.maps.LatLng
    ): google.maps.marker.AdvancedMarkerElement => {
      let markerFormatter: ClusterMarkerFormatter;

      if (
        marker.useClusterContentFormatter &&
        options?.clusterMarkerFormatters &&
        options.clusterMarkerFormatters[marker.useClusterContentFormatter]
      ) {
        const formatter =
          options.clusterMarkerFormatters[marker.useClusterContentFormatter];

        assertValue(
          formatter,
          'dynamicMapIntegration.ts > clusterMarkerFactory > formatter'
        );
        markerFormatter = formatter;
      } else {
        markerFormatter = defaultClusterMarkerFormatter;
      }

      const content = markerFormatter(marker, cluster, {
        integration: 'google',
      });
      const displayPriority = 0;

      const clusterMarker = new google.maps.marker.AdvancedMarkerElement({
        position,
        content,
      });

      clusterMarker.element.classList.add('google-marker-container');
      clusterMarker.element.style.zIndex = displayPriority.toString();

      return clusterMarker;
    };

    const mountedMap = new MountedGoogleMap(
      googleMapInstance,
      singleMarkerFactory,
      clusterMarkerFactory
    );

    return mountedMap;
  }

  private async loadMapsLibrary(
    options?: MapInitializationOptions
  ): Promise<void> {
    loaderStatusSubject.next('loading');

    const loader = new GoogleMapsLoader(GOOGLE_API_KEY, {
      language: options?.locale ?? 'en',
    });

    await Promise.all([
      loader.loadMapsApi,
      loader.loadLibrary('marker'),
      loader.loadLibrary('geometry'),
    ]);

    loaderStatusSubject.next('loaded');
  }
}
